'use client';

import { UserGender, UserType } from '@/types/modelEnums';
import type { User } from '@/types/models';
import { useEffect, useState } from 'react';
import { ensureAmplifyConfigured } from '../../utils/ensureAmplifyConfigured';

// Same key AuthContext writes the cached signed-in user under. Marketing routes
// never mount AppProviders/AuthContext, so this hook is how the static marketing
// navbar learns whether someone is logged in.
const AUTH_CACHE_KEY = 'r2h_auth_cache';
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h, matches AuthContext

interface CachedAuth {
  user: User | null;
  timestamp: number;
}

function readCachedUser(): User | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedAuth;
    if (!parsed?.user) return null;
    if (Date.now() - parsed.timestamp > CACHE_MAX_AGE_MS) return null;
    return parsed.user;
  } catch {
    return null;
  }
}

/**
 * Lightweight, post-hydration session check for the static marketing navbar.
 *
 * Returns `null` during SSR and the first client render so server/client markup
 * match (preserving ISR). After mount it reads the cached signed-in user for an
 * instant answer, then best-effort confirms the session against Amplify so a
 * stale cache (e.g. logged out in another tab) doesn't keep showing the
 * authenticated CTA.
 */
export function useMarketingUser(): User | null {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    let cancelled = false;

    // 1) Instant answer from the cache (no Amplify import on the marketing bundle).
    const cached = readCachedUser();
    if (cached && !cancelled) setUser(cached);

    // 2) Best-effort verification against the real Cognito session.
    (async () => {
      try {
        await ensureAmplifyConfigured();
        const { getCurrentUser, fetchUserAttributes } = await import('aws-amplify/auth');
        const currentUser = await getCurrentUser().catch(() => null);
        if (cancelled) return;

        if (!currentUser) {
          // No live session — drop any stale cached user.
          setUser(null);
          return;
        }

        const attributes = await fetchUserAttributes().catch(() => null);
        if (cancelled || !attributes) return;

        const verified: User = {
          id:
            (currentUser as { userId?: string; username?: string }).userId ||
            (currentUser as { username?: string }).username ||
            '',
          type: attributes['custom:type'] as UserType,
          role: attributes['custom:role']?.toLowerCase(),
          personal_info: {
            email: attributes['email'],
            first_name: attributes['custom:first_name'],
            last_name: attributes['custom:last_name'],
            gender: (attributes['custom:gender'] as UserGender) || undefined,
            age: attributes['custom:age'] ? Number(attributes['custom:age']) : undefined,
            institution: attributes['custom:institution'],
          },
        };
        setUser(verified);
      } catch {
        // Amplify unavailable / not configured — keep whatever the cache gave us.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return user;
}
